import { useState } from 'react'
import { Link } from 'react-router-dom'
import { formatEur, canReadArea, canWriteArea, hasRight } from '@wf/shared'
import { useAuth } from '../app/auth'
import { useAttendanceMe } from '../attendance/useAttendance'
import { localYmd } from '../orders/workshop'
import { useInvoices, useOpenItems, useOrdersList } from '../orders/useOrders'
import { GreetingHeader } from './GreetingHeader'
import { KpiTiles } from './KpiTiles'
import { MechanicWorkload } from './MechanicWorkload'
import { MyWorkToday } from './MyWorkToday'
import { Onboarding } from './Onboarding'
import { OpenItemsPreview } from './OpenItemsPreview'
import { computeKpis, mechanicLoads, myActiveOrders } from './overviewMetrics'
import { OverviewSearchResults } from './OverviewSearchResults'
import { PartsStatus } from './PartsStatus'
import { StatusBoard } from './StatusBoard'

/**
 * Die Übersicht — rollen-eigene Willkommens-Startseite und Default-Landung nach dem Login. Der Kopf
 * (Begrüßung/Uhr/Spruch) und die globale Suche sind für jede Rolle gleich; darunter komponiert sich das
 * Dashboard rein aus Berechtigungen (RBAC ehrlich): die UI lädt + zeigt nur, was die Rolle auch darf.
 *  - 'chef'        → Chef-Block (KPIs, Status-Board, Auslastung, Onboarding); Geld nur mit 'rechnung'.
 *  - 'eigene'/'werkstatt' → „Meine Arbeit heute" (nur eigene Aufträge/Aufgaben/Zeit, keine Umsatzzahlen).
 *  - 'einkauf'     → Teile-Status.
 */
export function OverviewPage() {
  const { user } = useAuth()
  const [query, setQuery] = useState('')

  // RBAC-Prädikate sind null-sicher (hasCap(null) === false) → vor dem Null-Guard berechenbar, damit ALLE
  // Hooks unbedingt laufen (Rules of Hooks). Geld-Endpunkte nur mit Recht laden (sonst 403/Leerlauf).
  const canChef = hasRight(user, 'produktivitaet:lesen')
  const canMoney = canReadArea(user, 'rechnungen')
  const isMechanic = hasRight(user, 'meine_auftraege') || canReadArea(user, 'werkstatt')
  const canPurchasing = canReadArea(user, 'einkauf')
  const canOnboard = canWriteArea(user, 'werkstatt_einstellungen')

  const ordersQuery = useOrdersList()
  const openItemsQuery = useOpenItems(canChef && canMoney)
  const invoicesQuery = useInvoices(canChef && canMoney)
  const attendanceMe = useAttendanceMe()

  if (!user) return null

  const orders = ordersQuery.data?.orders ?? []
  const now = new Date()
  const today = localYmd(now)
  const kpis = computeKpis(orders, today, now.getTime())
  const loads = mechanicLoads(orders)
  const searching = query.trim() !== ''
  // Ein Chef sieht „Meine Arbeit" nur, wenn er tatsächlich zugewiesene Aufträge hat (sonst leere Sektion);
  // ein reiner Mechaniker sieht sie immer (sein Hauptblick).
  const showMyWork = isMechanic && (!canChef || myActiveOrders(orders, user.id).length > 0)
  const showFallback = !searching && !canChef && !showMyWork && !canPurchasing

  return (
    <div className="wf-overview">
      <GreetingHeader name={user.name} />

      <input
        className="wf-input wf-overview__search"
        type="search"
        placeholder="Suchen: Kennzeichen, Fahrzeug, Kunde, Nummer …"
        aria-label="Aufträge global durchsuchen"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      {searching ? (
        <OverviewSearchResults query={query} user={user} />
      ) : (
        <>
          {canChef && canOnboard ? <Onboarding ordersCount={orders.length} /> : null}

          {canChef ? (
            <>
              <KpiTiles
                kpis={kpis}
                showMoney={canMoney}
                umsatzCents={invoicesQuery.data?.totalCents ?? null}
                paidCents={invoicesQuery.data?.paidCents ?? null}
              />
              <div className="wf-overview__cols">
                <MechanicWorkload loads={loads} />
                <div className="wf-overview__side">
                  <StatusBoard counts={kpis.statusCounts} />
                  {canMoney ? (
                    <div className="wf-ov-accentcard">
                      <div className="wf-ov-accentcard__label">Aktuell offen</div>
                      <div className="wf-ov-accentcard__value">{formatEur(kpis.openValueCents)}</div>
                      <div className="wf-ov-accentcard__sub">
                        offener Auftragswert · {kpis.activeCount} aktive Aufträge
                        {kpis.estimateOpenCount
                          ? ` · ${kpis.estimateOpenCount} ${kpis.estimateOpenCount === 1 ? 'Voranschlag' : 'Voranschläge'} offen`
                          : ''}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              {canMoney ? <OpenItemsPreview data={openItemsQuery.data} /> : null}
              <div className="wf-overview__quick">
                <Link className="wf-btn wf-btn--ghost" to="/archive">
                  Archiv ansehen
                </Link>
                {canMoney ? (
                  <Link className="wf-btn wf-btn--ghost" to="/invoices">
                    Rechnungsliste
                  </Link>
                ) : null}
              </div>
            </>
          ) : null}

          {showMyWork ? (
            <MyWorkToday orders={orders} userId={user.id} todaySeconds={attendanceMe.data?.todaySeconds} />
          ) : null}

          {canPurchasing ? <PartsStatus /> : null}

          {showFallback ? (
            <div className="wf-empty">
              <div className="wf-empty__text">Wähle oben einen Reiter, um loszulegen.</div>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
